import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react"

interface PageNavigatorInputProps {
  value: string
  onValueChange: (value: string) => void
}


export type InputComponentHandle = {
  focusInput: () => void
}

/**
 * @param {PageNavigatorInputProps} value - required
 * @param {PageNavigatorInputProps} onValueChange - required, change event handler
 * @returns PageNavigatorInput
 */
const PageNavigatorInput = forwardRef<InputComponentHandle, PageNavigatorInputProps>(({ value, onValueChange }: PageNavigatorInputProps, ref) => {

  const inputRef = useRef<HTMLInputElement>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [inputValue, setInputValue] = useState('')

  const handleOnKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // handle here on enter
    if (e.key === 'Enter') {
      setIsEditing(false)
      onValueChange(inputValue)
    }
  }

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value)
  }

  const handleOnBlur = () => {
    setIsEditing(false)
    onValueChange.call(null, inputValue)
  }

  const doEditText = () => {
    setIsEditing(true)
  }

  useImperativeHandle(ref, () => ({
    focusInput() {
      if (inputRef.current) {
        setIsEditing(true)
        inputRef.current.focus()
      }
    }
  }))

  useEffect(() => {
    if (inputValue !== value) {
      setInputValue(value)
    }
  }, [value])

  useEffect(() => {
    if (isEditing && inputRef?.current) {
      inputRef.current.focus()
    }
  }, [isEditing])

  return (
    <div className="flex min-w-0 items-center">
      <div
        onClick={doEditText}
        className={`h-6 min-w-0 cursor-text truncate type-heading-lg leading-6 ${isEditing ? 'hidden' : 'inline-block'}`}
      >
        {inputValue}
      </div>
      <input
        type={isEditing ? 'text' : 'hidden'}
        ref={inputRef}
        onKeyDown={handleOnKeyDown}
        onChange={handleOnChange}
        onBlur={handleOnBlur}
        className="h-6 min-w-0 w-full border-0 p-0 m-0 shadow-none type-heading-lg leading-6"
        value={inputValue}
        maxLength={50}
      />
    </div>
  )
})

export default PageNavigatorInput
